import * as THREE from 'three';
import {
    isSketchGroup,
    isSketchLineObject,
    type SketchLineObject,
} from '../../../mesh/sketch';/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/edit/revolve 层；
 * 2. 负责把场景选择解析为旋转凸台可持久化的旋转轴；
 * 3. 该解析器只处理轴语义，不负责轮廓、预览或提交。
 *
 * 文件职责：
 * 1. 支持草图线段作为旋转轴；
 * 2. 支持模型直线边作为旋转轴；
 * 3. 输出可写入命令历史的 axisReference，供编辑与重建链路复用。
 */

/**
 * 旋转轴数值。
 */
export interface RevolveAxisValue {
    /** 轴上一点。 */
    point: [number, number, number];
    /** 轴方向。 */
    direction: [number, number, number];
}

/**
 * 模型边线段快照。
 */
export interface RevolveModelEdgeSegment {
    /** 边起点。 */
    start: [number, number, number];
    /** 边终点。 */
    end: [number, number, number];
}

/**
 * 旋转轴解析结果。
 */
export interface BossRevolveAxisResolution {
    /** 可执行旋转轴。 */
    axis: RevolveAxisValue;
    /** 可持久化旋转轴引用。 */
    axisReference?: RevolveAxisReference;
    /** 属性栏显示标签。 */
    label: string;
    /** 本次选择的场景对象 ID。 */
    sourceObjectId: string;
}

/**
 * 旋转轴选择草稿。
 */
export interface BossRevolveSessionAxisSelection {
    /** 选中对象 ID。 */
    objectId: string;
    /** 若命中模型拓扑，则携带权威拓扑命中快照。 */
    topologyPick?: ResolvedTopologyPick | null;
}

/**
 * 模型边线段解析函数。
 */
export type RevolveModelEdgeSegmentResolver = (
    objectId: string,
    edgeIndex: number,
) => RevolveModelEdgeSegment | null;

/**
 * 旋转轴解析器。
 */
export class RevolveAxisResolver {
    /**
     * 从场景选择解析旋转轴。
     *
     * @param input 解析输入。
     * @returns 旋转轴解析结果；无法解析时返回 null。
     */
    public resolveFromSelection(input: {
        scene: THREE.Scene | null;
        selection: BossRevolveSessionAxisSelection;
        resolveModelEdgeSegment?: RevolveModelEdgeSegmentResolver;
    }): BossRevolveAxisResolution | null {
        const topologyAxis = this.resolveModelEdgeAxis(input.selection, input.resolveModelEdgeSegment);
        if (topologyAxis) {
            return topologyAxis;
        }

        const sceneObject = this.findSceneObjectById(input.scene, input.selection.objectId);
        return this.resolveFromSketchObject({
            object: sceneObject,
        });
    }

    /**
     * 从草图子实体绑定解析旋转轴。
     *
     * 设计说明：
     * 1. 草图重建时不能沿用旧命令中的轴数值；
     * 2. 必须回到绑定的草图线对象读取最新端点；
     * 3. 解析失败时由重建调度器保留旧命令，避免生成错误轴。
     *
     * @param input 草图对象解析输入。
     * @returns 旋转轴解析结果；无法解析时返回 null。
     */
    public resolveFromSketchBinding(input: {
        object: THREE.Object3D | null;
        sketchId: string;
        preserveDirection?: boolean;
    }): BossRevolveAxisResolution | null {
        const resolved = this.resolveFromSketchObject({
            object: input.object,
            forcedSketchId: input.sketchId,
            preserveDirection: input.preserveDirection,
        });
        return resolved;
    }

    /**
     * 从草图对象解析旋转轴。
     *
     * @param input 草图对象输入。
     * @returns 旋转轴解析结果；无法解析时返回 null。
     */
    public resolveFromSketchObject(input: {
        object: THREE.Object3D | null;
        forcedSketchId?: string;
        preserveDirection?: boolean;
    }): BossRevolveAxisResolution | null {
        const lineObject = this.resolveSketchLineObject(input.object);
        if (!lineObject || lineObject.isDrawingPreviewLine) {
            return null;
        }

        const sketchId = input.forcedSketchId ?? this.resolveSketchGroupId(lineObject);
        if (!sketchId) {
            return null;
        }

        const axis = this.buildAxisFromSketchLine(lineObject);
        if (!axis) {
            return null;
        }

        const axisReference: RevolveSketchEdgeAxisReference = {
            kind: 'sketch_edge',
            sourceRef: {
                ownerSketchId: sketchId,
                ownerObjectId: lineObject.persistentId,
                subEntityType: 'edge',
            },
            expectedGeometry: 'infinite_line',
            preserveDirection: input.preserveDirection ?? true,
        };

        return {
            axis,
            axisReference,
            label: `${this.resolveSketchName(lineObject)} / ${lineObject.name || '旋转轴'}`,
            sourceObjectId: lineObject.uuid,
        };
    }

    /**
     * 从模型拓扑边解析旋转轴。
     *
     * @param selection 当前轴选择。
     * @param resolveModelEdgeSegment 模型边线段解析函数。
     * @returns 旋转轴解析结果；无法解析时返回 null。
     */
    private resolveModelEdgeAxis(
        selection: BossRevolveSessionAxisSelection,
        resolveModelEdgeSegment?: RevolveModelEdgeSegmentResolver,
    ): BossRevolveAxisResolution | null {
        const topologyPick = selection.topologyPick ?? null;
        if (!topologyPick || topologyPick.kind !== 'edge' || !resolveModelEdgeSegment) {
            return null;
        }

        const segment = resolveModelEdgeSegment(topologyPick.bodyObjectId, topologyPick.topologyIndex);
        const axis = this.buildAxisFromSegment(segment);
        if (!axis) {
            return null;
        }

        const axisReference: RevolveModelEdgeAxisReference = {
            kind: 'model_edge',
            sourceRef: {
                ownerObjectId: topologyPick.bodyObjectId,
                subEntityType: 'edge',
                subEntityIndex: topologyPick.topologyIndex,
            },
            expectedGeometry: 'infinite_line',
            preserveDirection: true,
        };

        return {
            axis,
            axisReference,
            label: `模型边 ${topologyPick.topologyIndex + 1}`,
            sourceObjectId: topologyPick.bodyObjectId,
        };
    }

    /**
     * 从草图线对象构建旋转轴。
     *
     * @param lineObject 草图线对象。
     * @returns 旋转轴；线段退化时返回 null。
     */
    private buildAxisFromSketchLine(lineObject: SketchLineObject): RevolveAxisValue | null {
        const start = lineObject.startPoint.getPosition();
        const end = lineObject.endPoint.getPosition();
        return this.buildAxisFromPoints(start, end);
    }

    /**
     * 从模型边线段构建旋转轴。
     *
     * @param segment 模型边线段。
     * @returns 旋转轴；线段退化时返回 null。
     */
    private buildAxisFromSegment(segment: RevolveModelEdgeSegment | null | undefined): RevolveAxisValue | null {
        if (!segment) {
            return null;
        }
        return this.buildAxisFromPoints(
            new THREE.Vector3(...segment.start),
            new THREE.Vector3(...segment.end),
        );
    }

    /**
     * 从两点构建旋转轴。
     *
     * @param start 起点。
     * @param end 终点。
     * @returns 旋转轴；两点重合时返回 null。
     */
    private buildAxisFromPoints(start: THREE.Vector3, end: THREE.Vector3): RevolveAxisValue | null {
        const direction = end.clone().sub(start);
        if (direction.lengthSq() <= 1e-12) {
            return null;
        }
        direction.normalize();
        return {
            point: [start.x, start.y, start.z],
            direction: [direction.x, direction.y, direction.z],
        };
    }

    /**
     * 在场景中查找对象。
     *
     * @param scene 场景。
     * @param objectId 对象 ID。
     * @returns 场景对象；未找到时返回 null。
     */
    private findSceneObjectById(scene: THREE.Scene | null, objectId: string): THREE.Object3D | null {
        if (!scene) {
            return null;
        }
        let target: THREE.Object3D | null = null;
        scene.traverse((object) => {
            const runtimeObjectId = (object as THREE.Object3D & {objectId?: string}).objectId;
            if (object.uuid === objectId || object.name === objectId || runtimeObjectId === objectId) {
                target = object;
            }
        });
        return target;
    }

    /**
     * 从命中对象向上查找草图线对象。
     *
     * @param object 命中对象。
     * @returns 草图线对象；未命中时返回 null。
     */
    private resolveSketchLineObject(object: THREE.Object3D | null): SketchLineObject | null {
        let current: THREE.Object3D | null = object;
        while (current) {
            if (isSketchLineObject(current)) {
                return current;
            }
            current = current.parent;
        }
        return null;
    }

    /**
     * 解析草图组 ID。
     *
     * @param object 场景对象。
     * @returns 草图组 ID；不存在时返回 null。
     */
    private resolveSketchGroupId(object: THREE.Object3D): string | null {
        let current: THREE.Object3D | null = object;
        while (current) {
            if (isSketchGroup(current)) {
                return current.persistentId;
            }
            current = current.parent;
        }
        return null;
    }

    /**
     * 解析草图显示名称。
     *
     * @param object 场景对象。
     * @returns 草图显示名称。
     */
    private resolveSketchName(object: THREE.Object3D): string {
        let current: THREE.Object3D | null = object;
        while (current) {
            if (isSketchGroup(current)) {
                return current.name || '草图';
            }
            current = current.parent;
        }
        return '草图';
    }
}
